/*
 * Cart free-gift guard
 * --------------------
 * Keeps the designated free promotional products honest:
 *
 *   - removes them as soon as the qualifying subtotal (everything EXCEPT those
 *     free products) drops below the configured minimum,
 *   - holds each of them to one per cart,
 *   - locks their quantity widget so the customer cannot edit it,
 *
 * and tells the customer why whenever it changes something.
 *
 * Runs on every page because the cart drawer is available site-wide, and also
 * re-validates when the customer clicks "Checkout" — theme scripts cannot run
 * inside Shopify's hosted checkout without Shopify Plus, so the last chance to
 * correct the cart is immediately before the redirect.
 *
 * Optional overrides — define BEFORE this script loads:
 *
 *   window.SpeciesFreeGiftGuardConfig = {
 *     freeProductIds: [8582993772719, 8690320146607],
 *     minimumSubtotalCents: 5000,
 *     subtotalBasis: 'final',   // 'final' (after discounts) or 'original'
 *     checkoutDiscountPercent: 0,
 *       // A sitewide code that only gets applied on the way to checkout is
 *       // invisible to /cart.js, so the cart looks bigger than what the
 *       // customer will actually pay. Set the percentage here (e.g. 30 for a
 *       // 30%-off code) and the threshold is tested against the discounted
 *       // figure. Ignored when the cart already carries an applicable code,
 *       // so a code applied on the cart page is never counted twice.
 *     message: 'Free items have been removed because your order is below {{ minimum }}.',
 *     maxFreeQuantity: 1,       // how many of each free product a cart may hold
 *     lockQuantity: true,       // make the quantity widget read-only on those lines
 *     limitMessage: 'Free items are limited to one per order.',
 *     blockCheckoutOnce: true,  // show the notice instead of continuing to checkout
 *     noticeDuration: 7000,     // ms the notice stays on screen (0 = until dismissed)
 *     debug: false,
 *   };
 */
(() => {
  const DEFAULTS = {
    freeProductIds: [8582993772719, 8690320146607],
    minimumSubtotalCents: 5000,
    subtotalBasis: 'final',
    checkoutDiscountPercent: 0,
    message: 'Free items have been removed because your order is below {{ minimum }}.',
    maxFreeQuantity: 1,
    lockQuantity: true,
    limitMessage: 'Free items are limited to one per order.',
    blockCheckoutOnce: true,
    noticeDuration: 7000,
    debug: false,
  };

  const config = { ...DEFAULTS, ...(window.SpeciesFreeGiftGuardConfig || {}) };

  const FREE_PRODUCT_IDS = new Set((config.freeProductIds || []).map(Number).filter(Number.isFinite));
  const MINIMUM_CENTS = Number(config.minimumSubtotalCents) || 0;
  const MAX_FREE_QUANTITY = Math.max(1, Number(config.maxFreeQuantity) || 1);
  const CHECKOUT_DISCOUNT_PERCENT = Math.min(100, Math.max(0, Number(config.checkoutDiscountPercent) || 0));

  const LOCK_ATTR = 'data-free-gift-locked';

  // Only ever touch quantity widgets inside the cart, never the one on a
  // product page that happens to show the same variant.
  const CART_SCOPES = 'cart-items, cart-drawer-items, #main-cart-items, #CartDrawer, cart-drawer';

  // A third-party promo app could re-add the gift the moment we remove it. Stop
  // trading requests with it instead of looping forever.
  const BURST_LIMIT = 3;
  const BURST_WINDOW_MS = 15000;
  const BACKOFF_MS = 60000;

  // Passive checks (page load, tab focus) never need to run back to back.
  const PASSIVE_MIN_INTERVAL_MS = 3000;

  const REQUEST_TIMEOUT_MS = 6000;

  const OWN_REQUEST_FLAG = '__freeGiftGuardOwnRequest';

  const log = (...args) => config.debug && console.log('[free-gift-guard]', ...args);

  if (!FREE_PRODUCT_IDS.size) {
    log('no free product ids configured, guard disabled');
    return;
  }

  let inFlight = null;
  let scheduled = null;
  let lastCart = null;
  let lastPassiveRun = 0;
  let changeTimestamps = [];
  let disabledUntil = 0;
  let allowNextCheckoutClick = false;
  let allowNextCheckoutClickTimer = null;

  const root = () => window.Shopify?.routes?.root || '/';

  /* ------------------------------------------------------------------ *
   * Cart maths
   * ------------------------------------------------------------------ */

  const isFreeGift = (item) => FREE_PRODUCT_IDS.has(Number(item?.product_id));

  function lineTotal(item) {
    if (config.subtotalBasis === 'original') {
      return Number(item?.original_line_price ?? item?.line_price ?? 0);
    }

    const finalLine = item?.final_line_price ?? item?.line_price;
    if (finalLine != null) return Number(finalLine) || 0;

    return (Number(item?.price) || 0) * (Number(item?.quantity) || 0);
  }

  // True once Shopify has attached a usable discount code to the cart itself,
  // which means the line prices already reflect it.
  function cartCarriesDiscountCode(cart) {
    return (
      Array.isArray(cart?.discount_codes) &&
      cart.discount_codes.some((entry) => entry && entry.applicable !== false && entry.code)
    );
  }

  // Subtotal of everything the customer is actually paying for, i.e. excluding
  // the free promotional products themselves.
  function qualifyingSubtotal(cart) {
    const subtotal = (cart?.items || []).reduce(
      (sum, item) => (isFreeGift(item) ? sum : sum + lineTotal(item)),
      0
    );

    // 'original' deliberately measures list price, so no discount modelling.
    if (config.subtotalBasis === 'original') return subtotal;

    // A code that only lands at checkout never reaches /cart.js. Model it here
    // so the threshold tests what the customer will actually pay. If the cart
    // already carries a code, its effect is in the line prices — don't double up.
    if (CHECKOUT_DISCOUNT_PERCENT > 0 && !cartCarriesDiscountCode(cart)) {
      // Shopify rounds the discount amount down, then subtracts it.
      const discount = Math.floor((subtotal * CHECKOUT_DISCOUNT_PERCENT) / 100);
      return Math.max(0, subtotal - discount);
    }

    return subtotal;
  }

  function formatMoney(cents) {
    const amount = cents / 100;
    const currency = window.Shopify?.currency?.active || 'USD';
    const fractionDigits = cents % 100 === 0 ? 0 : 2;

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }).format(amount);
    } catch (e) {
      return `$${amount.toFixed(fractionDigits)}`;
    }
  }

  /* ------------------------------------------------------------------ *
   * Cart API
   * ------------------------------------------------------------------ */

  // Requests are tagged so the cart-mutation hook below can ignore the guard's
  // own writes without suppressing a concurrent update from another script.
  // Unknown properties on a request init object are ignored by the browser.
  async function ownFetch(url, options = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
      return await fetch(url, {
        credentials: 'same-origin',
        signal: controller.signal,
        ...options,
        [OWN_REQUEST_FLAG]: true,
      });
    } finally {
      clearTimeout(timer);
    }
  }

  async function getCart() {
    const res = await ownFetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`cart.js responded ${res.status}`);
    return res.json();
  }

  // Updates are keyed by line item key rather than line index, so they stay
  // correct even if the cart changed between reading and writing.
  async function applyUpdates(updates) {
    if (!updates || !Object.keys(updates).length) return null;

    const res = await ownFetch(`${root()}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    });

    if (!res.ok) throw new Error(`cart/update.js responded ${res.status}`);
    return res.json();
  }

  /* ------------------------------------------------------------------ *
   * Notice
   * ------------------------------------------------------------------ */

  function showNotice(message) {
    const existing = document.querySelector('.free-gift-guard-notice');
    if (existing) existing.remove();

    const notice = document.createElement('div');
    notice.className = 'free-gift-guard-notice';
    notice.setAttribute('role', 'status');
    notice.setAttribute('aria-live', 'polite');

    const text = document.createElement('p');
    text.className = 'free-gift-guard-notice__text';
    text.textContent = String(message).replace('{{ minimum }}', formatMoney(MINIMUM_CENTS));

    const dismiss = document.createElement('button');
    dismiss.type = 'button';
    dismiss.className = 'free-gift-guard-notice__close';
    dismiss.setAttribute('aria-label', 'Dismiss notification');
    dismiss.innerHTML =
      '<svg aria-hidden="true" focusable="false" viewBox="0 0 14 14" width="14" height="14">' +
      '<path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"/>' +
      '</svg>';

    const remove = () => notice.remove();
    dismiss.addEventListener('click', remove);

    notice.append(text, dismiss);
    document.body.appendChild(notice);

    // Let the element paint before transitioning it in.
    requestAnimationFrame(() => notice.classList.add('is-visible'));

    const duration = Number(config.noticeDuration);
    if (duration > 0) setTimeout(remove, duration);
  }

  /* ------------------------------------------------------------------ *
   * Enforcement
   * ------------------------------------------------------------------ */

  function noteChange() {
    const now = Date.now();
    changeTimestamps = changeTimestamps.filter((time) => now - time < BURST_WINDOW_MS);
    changeTimestamps.push(now);

    if (changeTimestamps.length >= BURST_LIMIT) {
      disabledUntil = now + BACKOFF_MS;
      changeTimestamps = [];
      console.warn(
        '[free-gift-guard] Free items keep coming back right after being corrected — pausing for',
        BACKOFF_MS / 1000,
        'seconds to avoid fighting another script.'
      );
    }
  }

  // One line per free product, and no more than MAX_FREE_QUANTITY of it. Any
  // extra line for the same product is dropped rather than merged, because the
  // promo app may have added it with its own line properties.
  function quantityCapUpdates(giftLines) {
    const updates = {};
    const kept = new Set();

    giftLines.forEach((item) => {
      const productId = Number(item.product_id);

      if (kept.has(productId)) {
        updates[item.key] = 0;
        return;
      }

      kept.add(productId);
      if (Number(item.quantity) > MAX_FREE_QUANTITY) updates[item.key] = MAX_FREE_QUANTITY;
    });

    return updates;
  }

  /* ------------------------------------------------------------------ *
   * Quantity lock
   * ------------------------------------------------------------------ */

  function lockQuantityWidget(input) {
    const widget = input.closest('quantity-input') || input.closest('.quantity') || input.parentElement;
    if (!widget || widget.hasAttribute(LOCK_ATTR)) return;

    widget.setAttribute(LOCK_ATTR, '');

    // readOnly, not disabled: the cart page posts every updates[] input, and
    // dropping one would shift the array against the other line items.
    input.readOnly = true;
    input.tabIndex = -1;
    input.setAttribute('aria-readonly', 'true');
    input.max = String(MAX_FREE_QUANTITY);

    widget.querySelectorAll('button').forEach((button) => {
      button.disabled = true;
      button.tabIndex = -1;
      button.setAttribute('aria-hidden', 'true');
    });
  }

  function lockGiftQuantities(cart) {
    if (!config.lockQuantity) return;

    const giftVariantIds = new Set();
    const giftLines = new Set();

    (cart?.items || []).forEach((item, index) => {
      if (!isFreeGift(item)) return;
      if (item.variant_id != null) giftVariantIds.add(String(item.variant_id));
      giftLines.add(String(index + 1));
    });

    if (!giftVariantIds.size) return;

    document.querySelectorAll(CART_SCOPES).forEach((scope) => {
      scope.querySelectorAll('.quantity__input, input[name="updates[]"]').forEach((input) => {
        const variantId = input.dataset.quantityVariantId;

        // Match on variant id where the theme provides it, and fall back to the
        // line index for a theme whose markup does not.
        const isGift = variantId ? giftVariantIds.has(String(variantId)) : giftLines.has(String(input.dataset.index));

        if (isGift) lockQuantityWidget(input);
      });
    });
  }

  // The theme re-renders the cart from scratch on every update, which throws
  // away the locks, so re-apply them whenever the cart markup changes.
  function observeCart() {
    if (!config.lockQuantity || typeof MutationObserver === 'undefined') return;

    const scopes = document.querySelectorAll('cart-drawer, #main-cart-items, cart-items');
    if (!scopes.length) return;

    let pending = null;
    const observer = new MutationObserver(() => {
      if (pending) return;
      pending = requestAnimationFrame(() => {
        pending = null;
        if (lastCart) lockGiftQuantities(lastCart);
      });
    });

    scopes.forEach((scope) => observer.observe(scope, { childList: true, subtree: true }));
  }

  // Resolves to true when the guard actually changed the cart.
  async function enforce() {
    if (inFlight) return inFlight;

    if (Date.now() < disabledUntil) {
      log('backing off');
      return false;
    }

    inFlight = (async () => {
      const cart = await getCart();
      lastCart = cart;

      const giftLines = (cart.items || []).filter(isFreeGift);
      if (!giftLines.length) return false;

      const subtotal = qualifyingSubtotal(cart);
      log('qualifying subtotal', subtotal, 'minimum', MINIMUM_CENTS);

      // Below the minimum the free items go entirely; at or above it they stay,
      // but capped at one each.
      const belowMinimum = subtotal < MINIMUM_CENTS;
      const updates = belowMinimum
        ? giftLines.reduce((acc, item) => Object.assign(acc, { [item.key]: 0 }), {})
        : quantityCapUpdates(giftLines);

      if (!Object.keys(updates).length) {
        lockGiftQuantities(lastCart);
        return false;
      }

      const updatedCart = await applyUpdates(updates);
      if (!updatedCart) {
        lockGiftQuantities(lastCart);
        return false;
      }

      lastCart = updatedCart;
      noteChange();
      showNotice(belowMinimum ? config.message : config.limitMessage);

      // Let the theme's own cart components re-render (cart page and drawer);
      // the observer re-applies the quantity locks once they have.
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'free-gift-guard', cartData: updatedCart });
      }

      lockGiftQuantities(lastCart);
      return true;
    })()
      .catch((error) => {
        log('enforce failed', error);
        return false;
      })
      .finally(() => {
        inFlight = null;
      });

    return inFlight;
  }

  function scheduleEnforce(delay = 150) {
    if (scheduled) clearTimeout(scheduled);
    scheduled = setTimeout(() => {
      scheduled = null;
      enforce();
    }, delay);
  }

  function passiveEnforce() {
    const now = Date.now();
    if (now - lastPassiveRun < PASSIVE_MIN_INTERVAL_MS) return;
    lastPassiveRun = now;
    scheduleEnforce(0);
  }

  /* ------------------------------------------------------------------ *
   * Triggers
   * ------------------------------------------------------------------ */

  const CART_MUTATION_PATHS = ['/cart/add', '/cart/change', '/cart/update', '/cart/clear'];
  const isCartMutation = (url) => CART_MUTATION_PATHS.some((path) => String(url || '').includes(path));

  // Any script on the page can change the cart — including the promo app that
  // adds the free items — so watch the network rather than only theme events.
  const originalFetch = window.fetch;
  window.fetch = function (...args) {
    const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
    const watch = !args[1]?.[OWN_REQUEST_FLAG] && isCartMutation(url);

    const request = originalFetch.apply(this, args);
    if (!watch) return request;

    return request.then((response) => {
      scheduleEnforce();
      return response;
    });
  };

  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this.__freeGiftGuardUrl = url || '';
    return originalOpen.call(this, method, url, ...rest);
  };

  XMLHttpRequest.prototype.send = function (...args) {
    if (isCartMutation(this.__freeGiftGuardUrl)) {
      this.addEventListener('loadend', () => scheduleEnforce());
    }
    return originalSend.apply(this, args);
  };

  // Theme components (cart page and drawer) announce their own updates here.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.source === 'free-gift-guard') return;
      scheduleEnforce();
    });
  }

  // Last check before Shopify's hosted checkout, which theme scripts cannot reach.
  document.addEventListener(
    'click',
    (event) => {
      if (!config.blockCheckoutOnce) return;

      const button = event.target.closest?.('button[name="checkout"], input[name="checkout"], a[href$="/checkout"]');
      if (!button) return;
      if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;

      if (allowNextCheckoutClick) {
        allowNextCheckoutClick = false;
        clearTimeout(allowNextCheckoutClickTimer);
        return;
      }

      // Hold the click so the cart can be corrected first. Other checkout
      // listeners run on the bubble phase, so stop propagation here as well.
      event.preventDefault();
      event.stopImmediatePropagation();

      enforce().then((removed) => {
        // Something was removed: keep the customer on the cart so they can see
        // the notice and decide whether to top the order back up.
        if (removed) return;

        allowNextCheckoutClick = true;
        clearTimeout(allowNextCheckoutClickTimer);
        allowNextCheckoutClickTimer = setTimeout(() => {
          allowNextCheckoutClick = false;
        }, 5000);

        button.click();
      });
    },
    true
  );

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') passiveEnforce();
  });

  window.addEventListener('pageshow', passiveEnforce);

  function init() {
    observeCart();
    passiveEnforce();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.SpeciesFreeGiftGuard = { enforce, config };

  log('installed', {
    freeProductIds: [...FREE_PRODUCT_IDS],
    minimumCents: MINIMUM_CENTS,
    maxFreeQuantity: MAX_FREE_QUANTITY,
    checkoutDiscountPercent: CHECKOUT_DISCOUNT_PERCENT,
  });
})();
